/* eslint-disable max-len, object-curly-newline, react/jsx-one-expression-per-line, newline-per-chained-call, react/jsx-wrap-multilines, implicit-arrow-linebreak, function-paren-newline, operator-linebreak, react/jsx-curly-newline */
import React, { useContext, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Autorenew from '@mui/icons-material/Autorenew';
import Close from '@mui/icons-material/Close';
import AssignmentTurnedInOutlined from '@mui/icons-material/AssignmentTurnedInOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import NavigationLayout from '../../components/NavigationLayout';
import { NotificationContext } from '../../contexts/Notification';
import { UserContext } from '../../contexts/User';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';

/*
 * Chart Abstraction — QA Queue (member portal). Scoped to the signed-in
 * reviewer: only their auto-assigned charts, from /chart-abstraction/qa-queue.
 * Approve (Complete) or send back (Deny → returns to the rep to review again).
 */

const pad = (n) => String(n).padStart(2, '0');
const fmt = (s) => `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
const relTime = (ms) => {
  if (!ms) return '';
  const m = Math.max(0, Math.floor((Date.now() - ms) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const card = { background: '#fff', border: '1px solid var(--dark-6, #E0E1E6)', borderRadius: 12, boxShadow: '0 1px 2px rgba(14,30,64,0.04)' };
const cardHead = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 8px' };
const cardTitle = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' };
const countPill = { fontSize: 12, fontWeight: 600, color: 'var(--dark-3, #546077)', background: 'var(--dark-7, #ECECF0)', borderRadius: 20, padding: '1px 9px' };
const rowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 20px', borderTop: '1px solid var(--dark-7, #ECECF0)' };
const idStyle = { fontSize: 14, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' };
const subStyle = { fontSize: 12.5, color: 'var(--dark-3, #546077)', marginTop: 2 };
const pill = (bg, fg) => ({ display: 'inline-flex', alignItems: 'center', fontSize: 12.5, fontWeight: 600, borderRadius: 20, padding: '3px 11px', background: bg, color: fg, whiteSpace: 'nowrap' });

function ChartAbstractionQaQueue() {
  const { displayNotification } = useContext(NotificationContext);
  const { activeWorkspace } = useContext(UserContext);
  const workspaceId = activeWorkspace?.id;

  const [awaiting, setAwaiting] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [denied, setDenied] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denyTarget, setDenyTarget] = useState(null);
  const [denyReason, setDenyReason] = useState('');
  const [acting, setActing] = useState(false);

  const fetchQueue = async (showSpinner = false) => {
    if (!workspaceId) return;
    if (showSpinner) setLoading(true);
    const res = await sendRequest({ method: 'get', url: API.CHART_ABSTRACTION.qaQueue(workspaceId) });
    if (res.success) {
      setAwaiting(res.data.awaiting || []);
      setCompleted(res.data.completed || []);
      setDenied(res.data.denied || []);
    } else {
      displayNotification({ message: res.data?.error || 'Failed to load your QA queue', severity: 'error' });
    }
    if (showSpinner) setLoading(false);
  };

  useEffect(() => {
    if (!workspaceId) return undefined;
    fetchQueue(true);
    const poll = setInterval(() => fetchQueue(false), 15000);
    return () => clearInterval(poll);
  }, [workspaceId]);

  const handleComplete = async (item) => {
    if (acting) return;
    setActing(true);
    const res = await sendRequest({ method: 'post', url: API.CHART_ABSTRACTION.qaComplete(workspaceId, item.id) });
    setActing(false);
    if (res.success) {
      displayNotification({ message: `${item.recordId} signed off as QA complete`, severity: 'success' });
      fetchQueue(false);
    } else {
      displayNotification({ message: res.data?.error || 'Could not complete the chart', severity: 'error' });
    }
  };

  const confirmDeny = async () => {
    if (!denyTarget || !denyReason.trim() || acting) return;
    const item = denyTarget;
    setActing(true);
    const res = await sendRequest({ method: 'post', url: API.CHART_ABSTRACTION.qaDeny(workspaceId, item.id), data: { reason: denyReason.trim() } });
    setActing(false);
    if (res.success) {
      displayNotification({ message: `${item.recordId} sent back to ${item.repName} to review again`, severity: 'info' });
      setDenyTarget(null); setDenyReason('');
      fetchQueue(false);
    } else {
      displayNotification({ message: res.data?.error || 'Could not deny the chart', severity: 'error' });
    }
  };

  return (
    <NavigationLayout title="Chart Abstraction - QA Queue" subtitle="Review and sign off the charts assigned to you">
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}><CircularProgress size={30} /></div>
      ) : (
        <div style={{ marginTop: 24, fontFamily: 'Inter, sans-serif', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {/* Awaiting QA */}
            <div style={{ ...card, flex: 1, minWidth: 320 }}>
              <div style={cardHead}>
                <span style={cardTitle}><AssignmentTurnedInOutlined style={{ fontSize: 18, color: 'var(--blue-2, #2563eb)' }} />Awaiting QA</span>
                <span style={countPill}>{awaiting.length}</span>
              </div>
              <div style={{ padding: '0 0 8px' }}>
                {awaiting.length === 0 ? (
                  <div style={{ padding: '28px 20px', textAlign: 'center', color: 'var(--dark-4, #7D8698)', fontSize: 14 }}>All caught up — nothing awaiting your review.</div>
                ) : awaiting.map((c, i) => (
                  <div key={c.id} style={{ ...rowStyle, borderTop: i === 0 ? 'none' : rowStyle.borderTop }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={idStyle}>{c.recordId}</div>
                      <div style={subStyle}>{`Abstracted by ${c.repName} · ${fmt(c.onChartSeconds)} on chart · completed ${relTime(c.completedAtMs)}`}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <Button size="small" variant="contained" disabled={acting} onClick={() => handleComplete(c)} sx={{ textTransform: 'none', backgroundColor: 'var(--green-3, #53A551)', '&:hover': { backgroundColor: 'var(--green-2, #378A35)' } }}>Complete</Button>
                      <Button size="small" variant="outlined" disabled={acting} onClick={() => { setDenyTarget(c); setDenyReason(''); }} sx={{ textTransform: 'none', color: 'var(--red-2, #CC4525)', borderColor: '#e6c3ba' }}>Deny</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Denied by QA */}
            <div style={{ ...card, flex: 1, minWidth: 320 }}>
              <div style={cardHead}>
                <span style={cardTitle}><Autorenew style={{ fontSize: 18, color: 'var(--red-2, #CC4525)' }} />Denied by QA</span>
                <span style={countPill}>{denied.length}</span>
              </div>
              <div style={{ padding: '0 0 8px' }}>
                {denied.length === 0 ? (
                  <div style={{ padding: '28px 20px', textAlign: 'center', color: 'var(--dark-4, #7D8698)', fontSize: 14 }}>No charts sent back. Denied charts return to the abstraction rep&apos;s queue for rework.</div>
                ) : denied.map((c, i) => (
                  <div key={c.id} style={{ ...rowStyle, borderTop: i === 0 ? 'none' : rowStyle.borderTop }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={idStyle}>{c.recordId}</div>
                      <div style={{ ...subStyle, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{`Back to ${c.repName} · ${c.reason}`}</div>
                    </div>
                    <span style={pill('#FCECE7', 'var(--red-2, #CC4525)')}>Denied</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* QA completed */}
          <div style={card}>
            <div style={cardHead}>
              <span style={cardTitle}><CheckCircleOutline style={{ fontSize: 18, color: 'var(--green-1, #256D23)' }} />QA completed</span>
              <span style={countPill}>{completed.length}</span>
            </div>
            <div style={{ padding: '0 0 8px' }}>
              {completed.length === 0 ? (
                <div style={{ padding: '10px 20px 16px', color: 'var(--dark-3, #546077)', fontSize: 14 }}>No charts signed off yet.</div>
              ) : completed.map((c, i) => (
                <div key={c.id} style={{ ...rowStyle, borderTop: i === 0 ? 'none' : rowStyle.borderTop }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={idStyle}>{c.recordId}</div>
                    <div style={subStyle}>{`Abstracted by ${c.repName} · ${fmt(c.onChartSeconds)} on chart · reviewed ${relTime(c.reviewedAtMs)}`}</div>
                  </div>
                  <span style={pill('#E7F6E6', 'var(--green-1, #256D23)')}>QA complete</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Dialog open={!!denyTarget} onClose={() => setDenyTarget(null)} maxWidth="xs" fullWidth>
        <div style={{ padding: '20px 24px', fontFamily: 'Inter, sans-serif' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <h5 style={{ fontSize: 18, fontWeight: 600, color: 'var(--dark-1, #0E1E40)', margin: 0 }}>Deny this chart</h5>
            <IconButton size="small" onClick={() => setDenyTarget(null)}><Close style={{ fontSize: 20 }} /></IconButton>
          </div>
          {denyTarget && (
            <div style={{ display: 'flex', gap: 8, padding: '10px 12px', borderRadius: 8, marginBottom: 16, background: '#FDF1E3', border: '1px solid #f2d9b3' }}>
              <Autorenew style={{ fontSize: 18, color: '#B3650F', flexShrink: 0 }} />
              <p style={{ fontSize: 13.5, color: '#8a5a12', margin: 0 }}>
                {'Denying '}
                <strong>{denyTarget.recordId}</strong>
                {' will send it back to '}
                <strong>{denyTarget.repName}</strong>
                {' (abstraction rep) to review again.'}
              </p>
            </div>
          )}
          <TextField
            label="Reason for denial"
            multiline
            minRows={3}
            fullWidth
            value={denyReason}
            onChange={(e) => setDenyReason(e.target.value)}
            placeholder="e.g. Diagnosis codes need to be re-checked against the source document"
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
            <Button variant="outlined" onClick={() => setDenyTarget(null)} sx={{ textTransform: 'none', color: 'var(--dark-2, #293855)', borderColor: 'var(--dark-6, #E0E1E6)' }}>Cancel</Button>
            <Button variant="contained" disabled={!denyReason.trim() || acting} startIcon={<Autorenew />} onClick={confirmDeny} sx={{ textTransform: 'none', backgroundColor: 'var(--red-2, #CC4525)', '&:hover': { backgroundColor: '#a8391d' } }}>Deny &amp; send back</Button>
          </div>
        </div>
      </Dialog>
    </NavigationLayout>
  );
}

export default ChartAbstractionQaQueue;
